import { memo } from 'react';
import { Mail, MapPin, CalendarDays, Copy, CheckCheck } from 'lucide-react';
import { highlightMatches } from '../../utils';
import { openInGoogleMaps } from '../../utils/formatUtils';
import { CallButton } from '@/components/calling/CallButton';
import { formatAddress } from '@/utils/leadCard';
import type { Customer } from '../../types';
import { isPhoneMissing } from '@shared/phone-quality';

interface PipelineCardContactProps {
  customer: Customer;
  highlightTerms: string[];
  isCopied: Record<string, boolean>;
  copyToClipboard: (text: string | null | undefined, id: string) => void;
}

/**
 * Contact information section for pipeline cards
 * Displays email, phone, address with copy and action buttons
 *
 * Props are threaded from parent to avoid usePipelineContext() -
 * this allows React.memo on PipelineCard to actually prevent re-renders
 */
export const PipelineCardContact = memo(function PipelineCardContact({ customer, highlightTerms, isCopied, copyToClipboard }: PipelineCardContactProps) {
  const fullAddress = formatAddress(customer.address, customer.postcode);

  return (
    <div className="card-contact-row">
      {/* Blank or placeholder phone: an honest, visible gap instead of a dead number */}
      {isPhoneMissing(customer.phone) && (
        <span
          className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
          title="No usable phone number yet. Add it when the customer shares it."
          data-testid={`phone-missing-${customer.id}`}
        >
          Phone missing
        </span>
      )}

      {/* Phone - always shown first if available with click-to-call */}
      {customer.phone && !isPhoneMissing(customer.phone) && (
        <div className="flex items-center gap-1 group">
          <div onClick={(e) => e.stopPropagation()}>
            <CallButton
              customerId={customer.id}
              customerName={customer.name}
              customerPhone={customer.phone}
              size="icon"
              variant="ghost"
              showLabel={false}
              className="h-5 w-5 p-0 text-green-500 hover:text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-950/50"
            />
          </div>
          <span className="truncate max-w-[70px]">
            {highlightMatches(customer.phone, highlightTerms)}
          </span>
          <button
            type="button"
            className="focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(customer.phone, `phone-${customer.id}`);
            }}
            title="Copy phone"
          >
            {isCopied[`phone-${customer.id}`] ? (
              <CheckCheck className="h-2.5 w-2.5 text-green-500" />
            ) : (
              <Copy className="h-2.5 w-2.5 text-slate-400" />
            )}
          </button>
        </div>
      )}

      {/* Email */}
      {customer.email && (
        <div className="flex items-center gap-1 group">
          <Mail className="h-3 w-3 flex-shrink-0 text-blue-500 dark:text-blue-400" />
          <span className="truncate max-w-[100px]">
            {highlightMatches(customer.email, highlightTerms)}
          </span>
          <button
            type="button"
            className="focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(customer.email, `email-${customer.id}`);
            }}
            title="Copy email"
          >
            {isCopied[`email-${customer.id}`] ? (
              <CheckCheck className="h-2.5 w-2.5 text-green-500" />
            ) : (
              <Copy className="h-2.5 w-2.5 text-slate-400" />
            )}
          </button>
        </div>
      )}

      {/* Address - show full address with copy and map buttons */}
      {(customer.address || customer.postcode) && (
        <div className="flex items-center gap-1 group">
          <MapPin className="h-3 w-3 flex-shrink-0 text-slate-400" />
          <span className="truncate max-w-[140px]" title={fullAddress || ''}>
            {fullAddress}
          </span>
          <button
            type="button"
            className="focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(fullAddress || '', `address-${customer.id}`);
            }}
            title="Copy address"
          >
            {isCopied[`address-${customer.id}`] ? (
              <CheckCheck className="h-2.5 w-2.5 text-green-500" />
            ) : (
              <Copy className="h-2.5 w-2.5 text-slate-400" />
            )}
          </button>
          <button
            type="button"
            className="focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              openInGoogleMaps(fullAddress || '');
            }}
            title="View on map"
          >
            <MapPin className="h-2.5 w-2.5 text-blue-500" />
          </button>
        </div>
      )}

      {/* Date - very compact */}
      {customer.createdAt && (
        <div className="flex items-center gap-1 text-slate-400 dark:text-slate-500 ml-auto">
          <CalendarDays className="h-3 w-3 flex-shrink-0" />
          <span>
            {(() => {
              const ts = customer.createdAtTimestamp || new Date(customer.createdAt!).getTime();
              const daysAgo = Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
              if (daysAgo === 0) return 'Today';
              if (daysAgo === 1) return '1d';
              if (daysAgo < 7) return `${daysAgo}d`;
              if (daysAgo < 30) return `${Math.floor(daysAgo / 7)}w`;
              return `${Math.floor(daysAgo / 30)}mo`;
            })()}
          </span>
        </div>
      )}
    </div>
  );
});
